// src/components/meetings/MeetingScheduler.js
// Zero-Cost Meeting Scheduler Component with Date/Time Validation
// ============================================

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';

function MeetingScheduler({ userData, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: 'Monthly Society Meeting',
    meeting_date: '',
    meeting_time: '10:00',
    venue: 'Community Hall',
    meeting_type: 'monthly',
    agenda: ''
  });

  const [errors, setErrors] = useState({});

  const [agendaTemplate] = useState([
    '1. Previous meeting action items review',
    '2. Maintenance updates',
    '3. Financial report',
    '4. Security matters',
    '5. New proposals',
    '6. Any other business'
  ].join('\n'));

  useEffect(() => {
    // Set default date to next Sunday or today if it's Sunday
    const getDefaultDate = () => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      
      // If today is Sunday (0), use today; otherwise find next Sunday
      if (dayOfWeek === 0) {
        return today.toISOString().split('T')[0];
      } else {
        // Calculate days until next Sunday
        const daysUntilSunday = 7 - dayOfWeek;
        const nextSunday = new Date(today);
        nextSunday.setDate(today.getDate() + daysUntilSunday);
        return nextSunday.toISOString().split('T')[0];
      }
    };

    // Get current time + 1 hour for default meeting time
    const getDefaultTime = () => {
      const now = new Date();
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    setFormData(prev => ({
      ...prev,
      meeting_date: getDefaultDate(),
      meeting_time: getDefaultTime(),
      agenda: agendaTemplate
    }));
  }, [agendaTemplate]);

  const validateDateTime = () => {
    const errors = {};
    const now = new Date();
    const selectedDate = new Date(formData.meeting_date);
    
    // Set time for selected date
    if (formData.meeting_time) {
      const [hours, minutes] = formData.meeting_time.split(':');
      selectedDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    }

    // Get today's date at midnight for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateOnly = new Date(formData.meeting_date);
    selectedDateOnly.setHours(0, 0, 0, 0);

    // Check if date is in the past
    if (selectedDateOnly < today) {
      errors.date = 'Meeting date cannot be in the past';
    }
    
    // If meeting is today, check if time is in the past
    if (selectedDateOnly.getTime() === today.getTime()) {
      const nowTime = new Date();
      const selectedDateTime = new Date(formData.meeting_date);
      const [hours, minutes] = formData.meeting_time.split(':');
      selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      // Add 30 minutes buffer for meeting preparation
      nowTime.setMinutes(nowTime.getMinutes() + 30);
      
      if (selectedDateTime <= nowTime) {
        errors.time = 'Meeting time must be at least 30 minutes from now';
      }
    }

    // Check if meeting is too far in the future (optional: limit to 6 months)
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    if (selectedDate > sixMonthsFromNow) {
      errors.date = 'Meeting cannot be scheduled more than 6 months in advance';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate date and time
    const validationErrors = validateDateTime();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Clear any previous errors
    setErrors({});

    // Check for duplicate meetings on the same date/time
    try {
      const { data: existingMeetings, error: checkError } = await supabase
        .from('meetings')
        .select('id, title')
        .eq('meeting_date', formData.meeting_date)
        .eq('meeting_time', formData.meeting_time)
        .eq('status', 'scheduled');

      if (checkError) throw checkError;

      if (existingMeetings && existingMeetings.length > 0) {
        if (!window.confirm(`A meeting is already scheduled at this date and time. Do you want to continue?`)) {
          return;
        }
      }

      const meetingDate = new Date(formData.meeting_date);
      
      const { data, error } = await supabase
        .from('meetings')
        .insert([{
          ...formData,
          fiscal_month: meetingDate.getMonth() + 1,
          fiscal_year: meetingDate.getFullYear(),
          created_by: userData.id,
          status: 'scheduled',
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) throw error;

      alert('Meeting scheduled successfully!');
      onSave && onSave(data[0]);
      onClose && onClose();
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      alert('Failed to schedule meeting: ' + error.message);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (6 months from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6);
    return maxDate.toISOString().split('T')[0];
  };

  // Get minimum time if today is selected
  const getMinTime = () => {
    if (!formData.meeting_date) return '';
    
    const selectedDate = new Date(formData.meeting_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    // If selected date is today, return current time + 30 minutes
    if (selectedDate.getTime() === today.getTime()) {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 30);
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    return ''; // No minimum time for future dates
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '10px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          color: 'white',
          borderRadius: '10px 10px 0 0'
        }}>
          <h3 style={{ margin: 0 }}>📅 Schedule Meeting</h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Meeting Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              style={inputStyle}
              required
              placeholder="Enter meeting title"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Date *</label>
              <input
                type="date"
                value={formData.meeting_date}
                onChange={(e) => {
                  setFormData({...formData, meeting_date: e.target.value});
                  setErrors({...errors, date: ''});
                }}
                min={getMinDate()}
                max={getMaxDate()}
                style={{
                  ...inputStyle,
                  borderColor: errors.date ? '#dc3545' : '#e0e0e0'
                }}
                required
              />
              {errors.date && (
                <small style={{ color: '#dc3545', fontSize: '12px' }}>
                  {errors.date}
                </small>
              )}
            </div>
            <div>
              <label style={labelStyle}>Time *</label>
              <input
                type="time"
                value={formData.meeting_time}
                onChange={(e) => {
                  setFormData({...formData, meeting_time: e.target.value});
                  setErrors({...errors, time: ''});
                }}
                min={getMinTime()}
                style={{
                  ...inputStyle,
                  borderColor: errors.time ? '#dc3545' : '#e0e0e0'
                }}
                required
              />
              {errors.time && (
                <small style={{ color: '#dc3545', fontSize: '12px' }}>
                  {errors.time}
                </small>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Venue *</label>
            <input
              type="text"
              value={formData.venue}
              onChange={(e) => setFormData({...formData, venue: e.target.value})}
              style={inputStyle}
              required
              placeholder="Enter meeting venue"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Meeting Type *</label>
            <select
              value={formData.meeting_type}
              onChange={(e) => setFormData({...formData, meeting_type: e.target.value})}
              style={inputStyle}
              required
            >
              <option value="monthly">Monthly Meeting</option>
              <option value="emergency">Emergency Meeting</option>
              <option value="special">Special Meeting</option>
              <option value="agm">Annual General Meeting</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Agenda</label>
            <textarea
              value={formData.agenda}
              onChange={(e) => setFormData({...formData, agenda: e.target.value})}
              style={{...inputStyle, minHeight: '150px'}}
              placeholder="Enter meeting agenda points..."
            />
            <button
              type="button"
              onClick={() => setFormData({...formData, agenda: agendaTemplate})}
              style={{
                marginTop: '5px',
                padding: '5px 10px',
                background: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Use Template
            </button>
          </div>

          {/* Information box */}
          <div style={{
            padding: '10px',
            background: '#e3f2fd',
            borderRadius: '5px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#1976d2'
          }}>
            ℹ️ Meetings can be scheduled from today up to 6 months in advance. 
            For today's meetings, allow at least 30 minutes preparation time.
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '12px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Schedule Meeting
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: '600',
  fontSize: '14px',
  color: '#333'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '2px solid #e0e0e0',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.3s'
};

export default MeetingScheduler;